/* =====================================================================
   RETRY WITH EXPONENTIAL BACKOFF
   =====================================================================
   Generic async retry helper. Retries a failing async function with
   exponential delay between attempts.

   Usage:
     await retryWithBackoff(() => fetchSomething(), { retries: 3 });
===================================================================== */

const DEFAULTS = {
  retries: 3,
  baseMs: 300,
  factor: 2,
  onRetry: null,
};

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * @template T
 * @param {() => Promise<T>} fn
 * @param {{ retries?: number, baseMs?: number, factor?: number, onRetry?: (ctx: { attempt: number, totalRetries: number, delay: number, error: unknown }) => void }} [options]
 * @returns {Promise<T>}
 */
export async function retryWithBackoff(fn, options = {}) {
  const { retries, baseMs, factor, onRetry } = { ...DEFAULTS, ...options };

  let lastError;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;

      if (attempt < retries) {
        const delay = baseMs * Math.pow(factor, attempt);
        onRetry?.({ attempt: attempt + 1, totalRetries: retries, delay, error: err });
        await sleep(delay);
      }
    }
  }

  throw lastError;
}
